import type { EnvironmentConfig } from "~/shared/utils/environment-config";

import aksdev from "./env.aks.dev.json";
import aksstage from "./env.aks.stage.json";
import akshi from "./env.akshi.json";
import development from "./env.dev.json";
import local from "./env.local.json";
import production from "./env.prod.json";
import stage from "./env.stage.json";

// Updated EnvironmentStage
type EnvironmentStage =
	| "local"
	| "dev"
	| "stage"
	| "prod"
	| "aksdev"
	| "aksstage"
	| "akshi";

const deploymentType = import.meta.env["VITE_DEPLOYMENT_TYPE"] as string;

const isExternal = deploymentType?.trim() === "external";

const environmentMapping: Record<EnvironmentStage, EnvironmentConfig> = {
	local,
	dev: development,
	stage,
	prod: production,
	aksdev,
	aksstage,
	akshi,
};

/** pricing.apps.tql.com family
 *  preview.dev.pricing.apps.tql.com   => dev (with preview API URL)
 *  preview.stage.pricing.apps.tql.com => stage (with preview API URL)
 *  preview.pricing.apps.tql.com       => prod (with preview API URL)
 *  dev.pricing.apps.tql.com           => dev
 *  stage.pricing.apps.tql.com         => stage
 *  pricing.apps.tql.com               => prod
 */
const hostPricing =
	/^(?:(?<isPreview>preview)\.)?(?:(?<env>local|dev|stage|prod)\.)?pricing\.apps\.tql\.com$/i;

/**
 * <env>-trax.tqlapp.com (remapped dev/stage for aks)
 *   dev-trax.tqlapp.com    => aksdev
 *   stage-trax.tqlapp.com  => aksstage
 *   prod-trax.tqlapp.com   => prod
 *   akslo-trax.tqlapp.com  => akslo (optional: remove if unused)
 *   local-trax.tqlapp.com  => local
 *
 * trax(2<env>)?.tql.com (remapped dev/stage for aks)
 *   trax2dev.tql.com       => aksdev
 *   trax2stage.tql.com     => aksstage
 *   trax.tql.com           => prod
 */
const hostTqlappDash =
	/^(?:(?<env>local|dev|stage|prod|akshi|akslo)-trax\.tqlapp\.com|trax2(?<env2>dev|stage)\.tql\.com|trax\.tql\.com)$/i;

// Environment remapping for AKS
const envRemap: Partial<Record<EnvironmentStage, EnvironmentStage>> = {
	dev: "aksdev",
	stage: "aksstage",
};

function initializeEnvironmentConfig(): EnvironmentConfig {
	const { stage, isPreview } = parseEnvironmentStage();
	const config = {
		...environmentMapping[stage],
		environmentName: stage,
	};

	if (isExternal) {
		config.services.ltlQuoting = config.services.ltlQuotingExternal;
	}

	// For preview URLs, modify API endpoint to use same-origin
	if (isPreview) {
		const hostname = globalThis.location.hostname;
		const protocol = globalThis.location.protocol;
		config.services.ltlQuoting.url = `${protocol}//${hostname}/ltlquoting-api/api`;
		config.services.traxArApi.url = `${protocol}//${hostname}/trax-ar-api/api`;
	}

	// For local development with base path (internal deployment)
	if (
		stage === "local" &&
		globalThis.location.pathname.startsWith("/quotemgmt-ui")
	) {
		config.services.traxArApi.url = "/quotemgmt-ui/trax-ar-api/api";
	}

	return config;
}

function parseEnvironmentStage(): {
	stage: EnvironmentStage;
	isPreview: boolean;
} {
	const hostname = globalThis.location.hostname.toLowerCase();

	// localhost (and *.localhost) => local
	if (hostname === "localhost" || hostname.endsWith(".localhost")) {
		return { stage: "local", isPreview: false };
	}

	// Bare trax.tqlapp.com => akshi (main prod)
	if (hostname === "trax.tqlapp.com") {
		return { stage: "akshi", isPreview: false };
	}

	if (hostname === "trax.tql.com") {
		return { stage: "akshi", isPreview: false };
	}

	// pricing.apps.tql.com family
	let m = hostPricing.exec(hostname);
	if (m) {
		const isPreview = Boolean(m.groups?.["isPreview"]);
		const stage = (m.groups?.["env"] as EnvironmentStage | undefined) ?? "prod";
		return { stage, isPreview };
	}

	// <env>-trax.tqlapp.com / trax(2<env>)?.tql.com
	m = hostTqlappDash.exec(hostname);
	if (m) {
		const matched = m.groups?.["env"] ?? m.groups?.["env2"];
		const env = (matched as EnvironmentStage | undefined) ?? "prod";
		// Remap dev/stage for aksdev/aksstage
		const stage = envRemap[env] ?? env;
		return { stage, isPreview: false };
	}

	// Unknown / misconfigured host → fail closed, not prod
	throw new Error(`Could not parse environment from ${hostname}`);
}

globalThis._envconfig = initializeEnvironmentConfig();
